import { useGetPendingApprovalsQuery, useSubmitApprovalDecisionMutation } from "@/services/WorkFlowBuilderApi";
import { WorkflowApprovalItem } from "@/app/types/workflow";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { useState } from "react";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Textarea } from "@/components/ui/TextArea";
import useDocumentTitle from "@/hooks/UseDocumentTitle";
import { displayValue } from "@/utils/Helper";

export const WorkflowApprovals = () => {
  useDocumentTitle("Workflow Approvals");
  const { data, isLoading, refetch } = useGetPendingApprovalsQuery();
  const [submitDecision, { isLoading: isSubmitting }] = useSubmitApprovalDecisionMutation();

  const [selectedApproval, setSelectedApproval] = useState<WorkflowApprovalItem | null>(null);
  const [decisionType, setDecisionType] = useState<"approve" | "reject" | null>(null);
  const [comments, setComments] = useState("");

  const approvals = data?.data || [];

  const handleOpenDecision = (item: WorkflowApprovalItem, type: "approve" | "reject") => {
    setSelectedApproval(item);
    setDecisionType(type);
    setComments("");
  };

  const handleSubmit = async () => {
    if (!selectedApproval || !decisionType) return;
    try {
      await submitDecision({
        node_exec_id: selectedApproval.node_exec_id,
        approved: decisionType === "approve",
        comments: comments || undefined,
      }).unwrap();
      setSelectedApproval(null);
      setDecisionType(null);
      setComments("");
      refetch();
    } catch (e) {
      console.error("Failed to submit approval decision", e);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <Card className="p-4">
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading approvals...</p>
        ) : approvals.length === 0 ? (
          <p className="text-sm text-gray-500">No pending approvals assigned to you.</p>
        ) : (
          <div className="space-y-3">
            {approvals.map((item) => (
              <div
                key={item.node_exec_id}
                className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2"
              >
                <div className="space-y-0.5">
                  <div className="font-medium">
                    <button
                onClick={() => { window.location.href = `/admin/workflow-simulation?id=${item.instance_id}&workflow_id=${item.workflow_id}&workflow_version_id=${item.workflow_version_id}` }}
                className="underline"
              >
                {displayValue(item.workflow_name)}
              </button>
              </div>
                  <div className="text-xs text-gray-500">
                    Trigger: {item.trigger_source || "N/A"} · Submitted:{" "}
                    {item.submitted_at ? format(new Date(item.submitted_at), "yyyy-MM-dd HH:mm") : "N/A"}
                  </div>
                  <div className="text-xs text-gray-500">Status: {item.status}</div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleOpenDecision(item, "reject")}
                    className="border-red-200 text-red-600 hover:bg-red-50"
                  >
                    Reject
                  </Button>
                  <Button size="sm" onClick={() => handleOpenDecision(item, "approve")}>
                    Approve
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <AlertDialog open={!!selectedApproval} onOpenChange={(open) => !open && setSelectedApproval(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {decisionType === "approve" ? "Approve Workflow" : "Reject Workflow"}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Workflow: <span className="font-medium">{selectedApproval?.workflow_name}</span>
            </p>
            <Textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Add comments (optional)"
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit} disabled={isSubmitting}>
              {decisionType === "approve" ? "Approve" : "Reject"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

